import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getMyRegistrations } from '../api/registrations.api';
import { Registration } from '../types';
import LoadingSpinner from '../components/LoadingSpinner';

const DashboardRegistrationsPage: React.FC = () => {
  const [registrations, setRegistrations] = useState<Registration[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRegistrations = async () => {
      try {
        const response = await getMyRegistrations();
        setRegistrations(response.data || []);
      } catch (err) {
        // Silent fail for prototype.
      } finally {
        setLoading(false);
      }
    };

    fetchRegistrations();
  }, []);

  const getStatusBadge = (status: string) => {
    const statusLower = status.toLowerCase();
    if (statusLower === 'approved' || statusLower === 'success') {
      return 'bg-success';
    }
    if (statusLower === 'rejected' || statusLower === 'failed') {
      return 'bg-danger';
    }
    return 'bg-warning text-white';
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="container py-5">
      <div className="mb-3">
        <Link to="/dashboard" className="btn btn-outline-secondary btn-sm">
          <i className="bi bi-arrow-left me-1"></i>
          Back to Dashboard
        </Link>
      </div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h1 className="h3 section-title">My Registrations</h1>
          <p className="text-muted-soft mb-0">Track your registration status.</p>
        </div>
      </div>

      <div className="card card-surface">
        <div className="card-body">
          <div className="d-flex flex-wrap gap-2 mb-3">
            <span className="badge bg-warning text-white badge-status">Pending</span>
            <span className="badge bg-success badge-status">Approved</span>
            <span className="badge bg-danger badge-status">Rejected</span>
          </div>

          {registrations.length === 0 ? (
            <div className="alert alert-secondary mb-0">
              You have no active registrations yet.
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>Team Name</th>
                    <th>Event</th>
                    <th>Players</th>
                    <th>Status</th>
                    <th>Registered</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {registrations.map((reg) => (
                    <tr key={reg.id}>
                      <td>{reg.teamName}</td>
                      <td>
                        <Link to={`/events/${reg.eventId}`} className="text-decoration-none">
                          View Event
                        </Link>
                      </td>
                      <td>{reg.players?.length || 0}</td>
                      <td>
                        <span className={`badge ${getStatusBadge(reg.status)} badge-status`}>
                          {reg.status}
                        </span>
                      </td>
                      <td>
                        {reg.createdAt
                          ? new Date(reg.createdAt).toLocaleDateString()
                          : 'N/A'}
                      </td>
                      <td>
                        {reg.teamId && (
                          <Link
                            to={`/dashboard/idcards`}
                            className="btn btn-sm btn-outline-secondary"
                          >
                            ID Cards
                          </Link>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardRegistrationsPage;
